import { forwardRef, useCallback } from 'react';

import { styled } from '@spazious/dollhouse-theme';

import { useDataTableCell } from './hooks/index.js';
import { DataTableColumnDefinition, DataTableRow } from './DataTableTypes.js';

export type DataTableCellElement = HTMLDivElement;

export type DataTableCellProps<
  TRow extends DataTableRow,
  TContext = unknown
> = React.HTMLAttributes<DataTableCellElement> & {
  dense?: boolean;
  row?: TRow;
  column: DataTableColumnDefinition<TRow, TContext>;
  tabIndex: 0 | -1;
  columnIndex: number;
  rowIndex: number;
  context?: TContext;
  // renderCell?: React.FC<{
  //   row?: TRow;
  //   column: DataTableColumnDefinition<TRow, TContext>;
  //   formattedValue: string;
  //   value: any;
  //   context?: TContext;
  //   children?: React.ReactNode;
  // }>;
  onCellClick?: (
    evt: React.MouseEvent<HTMLDivElement>,
    meta: { row?: TRow; column: DataTableColumnDefinition<TRow, TContext> }
  ) => void;
};

export const DataTableCell = styled(
  forwardRef(
    <TRow extends DataTableRow, TContext = unknown>(
      {
        style,
        column,
        row,
        columnIndex,
        rowIndex,
        onCellClick,
        onClick,
        // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
        context,
        children,
        ...restProps
      }: DataTableCellProps<TRow, TContext>,
      ref: React.ForwardedRef<DataTableCellElement>
    ) => {
      const { innerRef, attributes } = useDataTableCell(columnIndex, rowIndex);

      const handleClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
        (evt) => {
          if (onCellClick) {
            onCellClick(evt, { column, row });
          }
          if (onClick) {
            onClick(evt);
          }
        },
        [onCellClick, onClick, column, row]
      );

      return (
        <div
          ref={ref}
          {...restProps}
          style={{
            ...style,
            ...attributes,
          }}
          onClick={handleClick}
        >
          <div ref={innerRef as React.Ref<HTMLDivElement>}>{children}</div>
        </div>
      );
    }
  )
).withConfig({
  displayName: 'DataTableCell',
})`
  padding: ${({ dense }) => (dense ? '4px' : '8px')};
  display: flex;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  align-items: center;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
  flex: 1;
  & > div {
    display: flex;
    align-items: center;
    padding: 8px;
  }
`;
